import { isTerminalStatus } from "./types.js";
import { isOverdueForDisplay, isDueSoon } from "./comparator.js";
/** The sentinel bucket key for tasks with no `caseRefs`. */
export const NO_CASE_BUCKET = "__no_case__";
/**
 * Group by case (mode `case`).
 *
 * **Plural `caseRefs` fan out** (redesign plan D-3): a task referencing three
 * cases appears under all three. The concept explicitly allows a task to span
 * cases and derives per-case views by filter (§3) — so a lawyer reviewing one
 * case must see every task touching it, even one shared with another matter.
 * The alternative (a "vários casos" bucket) hides work from the case that
 * needs it, which is the opposite of the point.
 *
 * Buckets are ordered by their most urgent member — since input is canonical,
 * that is simply the first task each bucket received. Tasks with no case go
 * last under `NO_CASE_BUCKET`.
 */
export function groupByCase(tasks) {
    const buckets = new Map();
    // Insertion order == first (most canonical) task per bucket, so iterating a
    // Map already yields "most urgent bucket first". No sort needed.
    for (const t of tasks) {
        const keys = t.caseRefs.length > 0 ? t.caseRefs : [NO_CASE_BUCKET];
        for (const k of keys) {
            let b = buckets.get(k);
            if (!b) {
                b = { key: k, caseId: k === NO_CASE_BUCKET ? null : k, tasks: [] };
                buckets.set(k, b);
            }
            b.tasks.push(t);
        }
    }
    const all = [...buckets.values()];
    // The no-case bucket always sinks to the bottom regardless of urgency: it is
    // a catch-all, not a case.
    return [
        ...all.filter((b) => b.key !== NO_CASE_BUCKET),
        ...all.filter((b) => b.key === NO_CASE_BUCKET),
    ];
}
/** Deadline bucket ids, in render order. */
export const DEADLINE_BUCKETS = ["overdue", "soon", "later", "undated", "terminal"];
/**
 * Group by deadline horizon (mode `deadline`).
 *
 * Terminal tasks are pulled out FIRST — a finished task has no live deadline,
 * and letting a `done` row with a past due date land under "Vencidas" is the
 * exact ordering-vs-display confusion `comparator.ts` exists to prevent.
 */
export function groupByDeadline(tasks, todayYmd, soonCutoff) {
    const out = {
        overdue: [],
        soon: [],
        later: [],
        undated: [],
        terminal: [],
    };
    for (const t of tasks) {
        if (isTerminalStatus(t.status))
            out.terminal.push(t);
        else if (isOverdueForDisplay(t, todayYmd))
            out.overdue.push(t);
        else if (isDueSoon(t, todayYmd, soonCutoff))
            out.soon.push(t);
        else if (t.dueDate != null)
            out.later.push(t);
        else
            out.undated.push(t);
    }
    return out;
}
//# sourceMappingURL=views.js.map